import React, {
  useState,
  useEffect,
  useRef,
  useCallback
} from "react";
import "./ChatBot.css";

const SUGGESTION_LIMIT = 3;
const MAX_RETRIES = 2;
const RETRY_BASE_MS = 600;

// WhatsApp config
const WHATSAPP_NUMBER = "16232123213"; // no +
const WHATSAPP_TEXT = encodeURIComponent(
  "Hello Admissions Express! I’d like to continue this conversation on WhatsApp."
);

const ChatBot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    {
      id: "m-0",
      sender: "bot",
      text: "Welcome to Admissions Express! How can I assist you today?",
      at: Date.now(),
    },
  ]);

  const [input, setInput] = useState("");
  const [faqList, setFaqList] = useState([]);
  const [typing, setTyping] = useState(false);
  const [sending, setSending] = useState(false);

  const chatEndRef = useRef(null);
  const abortRef = useRef(null);
  const faqsCacheRef = useRef(null);
  const lastQueryRef = useRef("");

  /* -------------------- helpers -------------------- */

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      chatEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    });
  }, []);

  useEffect(() => {
    if (isOpen) scrollToEnd();
  }, [messages, faqList, isOpen, scrollToEnd]);

  const nowId = () => `m-${Math.random().toString(36).slice(2, 9)}`;

  const fmtTime = (ts) =>
    new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  /* -------------------- FAQ preload -------------------- */

  useEffect(() => {
    let cancelled = false;

    const loadFaqs = async () => {
      try {
        if (!faqsCacheRef.current) {
          const res = await fetch(
            `${process.env.REACT_APP_FOR_UPLOADS_URL}/faqs`
          );
          const data = await res.json();
          if (!cancelled)
            faqsCacheRef.current = Array.isArray(data) ? data : [];
        }

        if (!cancelled)
          setFaqList(
            (faqsCacheRef.current || []).slice(0, SUGGESTION_LIMIT)
          );
      } catch {
        /* silent */
      }
    };

    loadFaqs();
    return () => {
      cancelled = true;
    };
  }, []);

  /* -------------------- suggestions while typing -------------------- */

  useEffect(() => {
    const value = input.trim();
    if (!value) {
      setFaqList([]);
      return;
    }

    const t = setTimeout(() => {
      const all = faqsCacheRef.current || [];
      const matches = all
        .filter((q) =>
          q.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, SUGGESTION_LIMIT);
      setFaqList(matches);
    }, 180);

    return () => clearTimeout(t);
  }, [input]);

  /* -------------------- backend request -------------------- */

  const postAsk = useCallback(async (payload) => {
    let attempt = 0;
    let lastErr = null;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    while (attempt <= MAX_RETRIES) {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_FOR_UPLOADS_URL}/ask`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: abortRef.current.signal,
          }
        );

        if (!res.ok) {
          if (
            (res.status === 429 || res.status >= 500) &&
            attempt < MAX_RETRIES
          ) {
            await new Promise((r) =>
              setTimeout(r, RETRY_BASE_MS * 2 ** attempt)
            );
            attempt++;
            continue;
          }
          throw new Error(`HTTP ${res.status}`);
        }

        return await res.json();
      } catch (err) {
        if (err.name === "AbortError") throw err;
        lastErr = err;
        attempt++;
      }
    }

    throw lastErr || new Error("Request failed");
  }, []);

  /* -------------------- send message -------------------- */

  const handleSendMessage = useCallback(
    async (textOverride) => {
      const text = (textOverride ?? input).trim();
      if (!text || sending) return;

      setSending(true);
      setTyping(true);
      setInput("");
      setFaqList([]);
      lastQueryRef.current = text;

      setMessages((prev) => [
        ...prev,
        { id: nowId(), sender: "user", text, at: Date.now() },
      ]);

      try {
        const data = await postAsk({ query: text });
        const answer =
          data?.answer?.trim() ||
          "I couldn’t find an answer. Please try rephrasing.";

        setMessages((prev) => [
          ...prev,
          { id: nowId(), sender: "bot", text: answer, at: Date.now() },
        ]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            id: nowId(),
            sender: "bot",
            text:
              "There was a connection error. Please try again shortly.",
            at: Date.now(),
          },
        ]);
      } finally {
        setTyping(false);
        setSending(false);
      }
    },
    [input, postAsk, sending]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  /* -------------------- UI -------------------- */

  if (!isOpen) return null;

  return (
    <div className="chatbot-container">
      <div
        className="chatbot"
        role="dialog"
        aria-label="Admissions Express Chatbot"
      >
        <div className="chatbot-header">
          Admissions Express Chatbot
          <button
            className="close-button"
            onClick={onClose}
            aria-label="Close chat"
          >
            ✕
          </button>
        </div>

        <div className="chatbot-body">
          {messages.map((m) => (
            <div key={m.id} className={`message ${m.sender}`}>
              <div className="message-meta">{fmtTime(m.at)}</div>
              <div
                dangerouslySetInnerHTML={{ __html: m.text }}
              />
            </div>
          ))}

          {faqList.length > 0 && (
            <div className="chatbot-recommendations">
              {faqList.map((faq, i) => (
                <button
                  key={`${faq}-${i}`}
                  onClick={() => handleSendMessage(faq)}
                  className="recommendation-button"
                >
                  {faq}
                </button>
              ))}
            </div>
          )}

          {typing && <div className="message bot">Typing…</div>}
          <div ref={chatEndRef} />
        </div>

        <div className="chatbot-footer">
          <textarea
            rows={1}
            placeholder="Type your message…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <button
            onClick={() => handleSendMessage()}
            disabled={!input.trim() || sending}
          >
            <i className="fa fa-paper-plane" />
          </button>

          {/* WhatsApp handoff */}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_TEXT}`}
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-btn"
            title="Continue on WhatsApp"
          >
            <i className="fab fa-whatsapp" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ChatBot;
